import React from "react";
import { Wallet, ChevronRight } from "lucide-react";

/**
 * WalletChip — desktop-first "Saldo a favor" pill shown at the far right of
 * the client tabs bar. On mobile (<640px) this is hidden because a much more
 * compact balance indicator lives directly inside the header (always visible
 * without any scrolling — see HeaderWalletBadge below).
 */
export function WalletChip({ active, amount, onClick, rewardPulse = false }) {
  const rounded = Math.round(Number(amount) || 0);
  const hasBalance = rounded > 0;
  const formatted = rounded.toLocaleString("es-VE");
  return (
    <button
      onClick={onClick}
      data-testid="client-tab-wallet"
      aria-label={`Saldo a favor: Bs ${formatted}`}
      className={`group relative ml-auto hidden sm:flex items-center gap-3 pl-2.5 pr-4 py-2 rounded-2xl transition-all overflow-hidden border ${
        active
          ? "border-emerald-400/60 bg-emerald-500/10 shadow-[0_0_0_1px_rgba(16,185,129,0.35),0_10px_35px_-12px_rgba(16,185,129,0.55)]"
          : "border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-emerald-400/5 to-cyan-500/10 hover:from-emerald-500/20 hover:via-emerald-400/10 hover:to-cyan-500/20 hover:border-emerald-400/50"
      }`}
    >
      {/* Subtle animated glow when balance exists */}
      {(hasBalance || rewardPulse) && (
        <span className="pointer-events-none absolute inset-0 rounded-2xl bg-gradient-to-r from-emerald-500/20 via-transparent to-cyan-500/20 opacity-70 blur-md" aria-hidden="true"></span>
      )}

      {/* Icon medallion */}
      <span className="relative flex items-center justify-center w-9 h-9 rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-500 text-white shadow-inner shadow-black/30 flex-shrink-0">
        <Wallet className="w-4 h-4" strokeWidth={2.5} />
        {hasBalance && (
          <>
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-ping"></span>
            <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400"></span>
          </>
        )}
      </span>

      {/* Label + amount */}
      <span className="relative flex flex-col items-start leading-tight">
        <span className="text-[10px] font-mono uppercase tracking-[0.15em] text-emerald-300/80">
          Saldo a favor
        </span>
        <span
          className={`text-base font-extrabold text-white font-mono tabular-nums transition-transform duration-500 ${rewardPulse ? "scale-110 text-emerald-200" : ""}`}
          data-testid="wallet-chip-amount"
        >
          Bs <span className={hasBalance ? "text-emerald-300" : "text-zinc-400"}>{formatted}</span>
        </span>
      </span>

      {rewardPulse && <span data-testid="wallet-reward-coins" aria-label="Recompensa acreditada" className="pointer-events-none absolute right-7 top-0 animate-bounce text-base">🪙</span>}

      <ChevronRight className="relative w-3.5 h-3.5 text-emerald-300/70 group-hover:translate-x-0.5 transition-transform" />
    </button>
  );
}

/**
 * HeaderWalletBadge — mobile-only compact balance pill that lives inside the
 * page header. It stays always-visible next to the cart icon so users never
 * lose sight of their saldo a favor on narrow viewports.
 */
export function HeaderWalletBadge({ amount, onClick, rewardPulse = false }) {
  const rounded = Math.round(Number(amount) || 0);
  const hasBalance = rounded > 0;
  const formatted = rounded.toLocaleString("es-VE");
  return (
    <button
      onClick={onClick}
      data-testid="header-wallet-badge"
      aria-label={`Saldo a favor: Bs ${formatted}`}
      className={`sm:hidden relative flex items-center gap-1.5 px-2 py-1.5 rounded-xl border font-mono text-xs transition-all ${
        hasBalance
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-200"
          : "border-[var(--ag7-border-soft)] bg-[var(--ag7-glass)] backdrop-blur-sm text-zinc-400"
      }`}
    >
      <Wallet className="w-3.5 h-3.5 flex-shrink-0" strokeWidth={2.5} />
      <span data-testid="header-wallet-amount" className={`tabular-nums font-black transition-transform duration-500 ${rewardPulse ? "scale-110 text-emerald-100" : ""}`}>Bs {formatted}</span>
      {hasBalance && (
        <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      )}
      {rewardPulse && <span data-testid="header-wallet-reward-coins" aria-label="Recompensa acreditada" className="pointer-events-none absolute -top-3 right-0 animate-bounce">🪙</span>}
    </button>
  );
}
